import { ColorPicker as AntColorPicker, ColorPickerProps, Flex } from "antd";
import styled from "styled-components";
import { PaletteResponse } from "pages/media/types";
import { PaintBucketIcon } from "@phosphor-icons/react";
import { useAntTheme } from "hooks/useAntTheme";
import { removeOpacityFromHex } from "utils";
import { useCallback, useMemo, useState } from "react";
import { useTranslate } from "#src/hooks";
import type { Color, PresetsItem } from "./colorTypes";
import { AggregationColor } from "./colorTypes";
import { useBoundStore } from "store/";
import { CustomColorPickerPanel } from "./CustomColorPickerPanel";
import type { GradientDefinition } from "types";
import type { GradientDirection } from "./types";
import { GRADIENT_GEOMETRIES } from "./types";
import { StyledColorPickerWrapper } from "./StyledColorPickerWrapper";
import {
  DEFAULT_COLOR,
  DEFAULT_TEXT_COLOR,
} from "pages/media/components/storyboardEditor/constants/";
import { radius } from "@libertify/ui";

declare global {
  interface EyeDropperResult {
    sRGBHex: string;
  }
  interface EyeDropper {
    open(): Promise<EyeDropperResult>;
  }
  interface Window {
    EyeDropper?: {
      prototype: EyeDropper;
      new (): EyeDropper;
    };
  }
}
export interface ColorPickerPropsBase extends ColorPickerProps {
  palette?: PaletteResponse;
  prefix?: boolean;
  title?: string;
  displayEyedropper?: boolean;
  showBorders?: boolean;
  initialGradientDirection?: GradientDirection;
  onChangeComplete?: (color: Color, gradient?: GradientDefinition) => void;
}

const buildGradientDefinition = (
  color: Color,
  direction: GradientDirection
): GradientDefinition => {
  const geometry = GRADIENT_GEOMETRIES[direction];
  return {
    geometry,
    stops: color.getColors().map((s) => ({
      color: s.color.toHexString(),
      position: s.percent / 100,
    })),
  };
};

// Returns the same color with full opacity (alpha forced to 100%), preserving
// gradient stops. Needed because antd collapses a gradient back to a single
// color on the next change when `disabledAlpha` is set and any stop has
// alpha < 100% (its internal `genAlphaColor` flattens the stops array).
const toOpaqueColor = (color: Color): Color =>
  color.isGradient()
    ? new AggregationColor(
        color.getColors().map((stop) => ({
          percent: stop.percent,
          color: new AggregationColor(
            removeOpacityFromHex(stop.color.toHexString())
          ),
        }))
      )
    : new AggregationColor(removeOpacityFromHex(color.toHexString()));

export const ColorPicker = ({
  palette,
  prefix = false,
  title,
  displayEyedropper = true,
  showBorders = true,
  presets: _presets,
  initialGradientDirection = "left-right",
  onChangeComplete,
  value,
  ...colorPickerProps
}: ColorPickerPropsBase) => {
  const { theme } = useAntTheme();
  const t = useTranslate();
  const [colorMode, setColorMode] = useState<ColorPickerProps["mode"]>(() => {
    const color = new AggregationColor(value || "");
    return color.isGradient() ? "gradient" : "single";
  });

  const [gradientDirection, setGradientDirection] = useState<GradientDirection>(
    initialGradientDirection
  );

  const [selectedColor, setSelectedColor] = useState<Color | null>(null);
  const { usedColors, addUsedColor } = useBoundStore((state) => ({
    usedColors: state.appState.layoutSettings.usedColors,
    addUsedColor: state.addUsedColor,
  }));

  const presets: PresetsItem[] = useMemo(() => {
    const usedColorsPresets =
      usedColors?.length > 0
        ? [
            {
              label: t("components.templates.settings.colorPicker.usedColors"),
              colors: usedColors,
              key: "used-colors",
              defaultOpen: true,
            },
          ]
        : [];

    if (_presets) return [..._presets, ...usedColorsPresets];
    if (!palette) return usedColorsPresets;
    return [...usedColorsPresets];
  }, [_presets, palette, usedColors, t]);

  const handleColorModeChange = useCallback(
    (newMode: ColorPickerProps["mode"]) => {
      setColorMode(newMode);
      const color = new AggregationColor(value || "");

      if (newMode === "gradient") {
        setGradientDirection(initialGradientDirection);
        // With alpha disabled, antd flattens a gradient to a single color on
        // the next change if any stop has alpha < 100%, kicking us back to
        // single mode. Force full opacity up front so the gradient survives.
        const baseColor = colorPickerProps.disabledAlpha
          ? toOpaqueColor(color)
          : color;
        const isWhiteOrDefault =
          baseColor.toHexString().toLowerCase() === DEFAULT_COLOR;

        const gradientColor = baseColor.isGradient()
          ? baseColor
          : new AggregationColor([
              { percent: 0, color: baseColor },
              {
                percent: 100,
                //if color is white, the second color stop is not visible enough
                color: isWhiteOrDefault ? DEFAULT_TEXT_COLOR : baseColor,
              },
            ]);
        setSelectedColor(gradientColor);
        onChangeComplete?.(
          gradientColor,
          buildGradientDefinition(gradientColor, initialGradientDirection)
        );
      } else if (newMode === "single" && color.isGradient()) {
        const singleColor = new AggregationColor(
          color.getColors()[0]?.color ?? DEFAULT_COLOR
        );
        setSelectedColor(singleColor);
        onChangeComplete?.(singleColor);
      }
    },
    [
      value,
      onChangeComplete,
      initialGradientDirection,
      colorPickerProps.disabledAlpha,
    ]
  );

  const handleGradientDirectionChange = useCallback(
    (direction: GradientDirection) => {
      setGradientDirection(direction);
      const color = new AggregationColor(value || "");
      if (color.isGradient()) {
        onChangeComplete?.(color, buildGradientDefinition(color, direction));
      }
    },
    [value, onChangeComplete]
  );

  const handleChangeComplete = useCallback(
    (colorValue: Color) => {
      setSelectedColor(colorValue);
      if (colorValue.isGradient()) {
        onChangeComplete?.(
          colorValue,
          buildGradientDefinition(colorValue, gradientDirection)
        );
      } else {
        if (colorMode === "gradient") setColorMode("single");
        onChangeComplete?.(colorValue);
      }
    },
    [onChangeComplete, colorMode, gradientDirection]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open === true || !selectedColor) return;
      const hasZeroOpacity = selectedColor.toHsb().a === 0;
      if (hasZeroOpacity) return;
      const selectedColorHex = selectedColor.toHexString();

      const isPresetColor = presets.some((preset) =>
        preset.colors.some((presetColor) => {
          if (typeof presetColor === "string") {
            return selectedColorHex.toLowerCase() === presetColor.toLowerCase();
          }
          return selectedColor.equals(presetColor);
        })
      );
      if (isPresetColor) return;
      addUsedColor(selectedColorHex);

      setSelectedColor(null);
    },
    [selectedColor, addUsedColor, presets]
  );

  const showTextValue = useMemo(() => {
    if (colorPickerProps.showText === true) {
      return (color: AggregationColor) =>
        removeOpacityFromHex(color.toHexString().toUpperCase()).replace(
          "#",
          ""
        );
    } else if (typeof colorPickerProps.showText === "function") {
      return colorPickerProps.showText;
    }
    return undefined;
  }, [colorPickerProps.showText]);

  return (
    <StyledColorPickerWrapper
      $showBorders={showBorders}
      align="center"
      justify="center"
      gap={6}
    >
      {prefix && (
        <PaintBucketIcon
          size={22}
          data-testid="paint-bucket-icon"
          color={theme.colorTextBase}
          style={{ verticalAlign: "text-top" }}
        />
      )}
      <AntColorPicker
        getPopupContainer={(triggerNode) => triggerNode}
        trigger="click"
        disabledAlpha={colorPickerProps.disabledAlpha ?? false}
        disabledFormat
        {...colorPickerProps}
        value={value}
        // don't show alpha if disabled
        showText={showTextValue}
        panelRender={(_panel, extra) => (
          <CustomColorPickerPanel
            pickerComponents={extra}
            title={title}
            allowClear={colorPickerProps.allowClear}
            displayEyedropper={displayEyedropper}
            handleChangeComplete={handleChangeComplete}
            palette={palette}
            hasCustomPresets={!!_presets}
            colorMode={colorMode}
            setColorMode={handleColorModeChange}
            gradientDirection={gradientDirection}
            onGradientDirectionChange={handleGradientDirectionChange}
            showGradient={colorPickerProps.mode?.includes("gradient")}
          />
        )}
        presets={presets}
        onChangeComplete={handleChangeComplete}
        // Gradient stops are only added by onChange
        onChange={colorMode === "gradient" ? handleChangeComplete : undefined}
        onOpenChange={(open) => {
          handleOpenChange(open);
          colorPickerProps.onOpenChange?.(open);
        }}
        mode={colorMode}
      />
    </StyledColorPickerWrapper>
  );
};

const wrappersProps = {
  style: { maxWidth: "fit-content" },
  align: "center",
  justify: "center",
  gap: 6,
};

export const RoundColorPicker = (props: ColorPickerPropsBase) => {
  return (
    <StyledCircleColorPicker
      data-testid="round-color-picker"
      {...wrappersProps}
    >
      <ColorPicker {...props} />
    </StyledCircleColorPicker>
  );
};

export const SquareColorPicker = (
  props: ColorPickerPropsBase & { triggerProps?: StyledSquareColorPickerProps }
) => {
  return (
    <StyledSquareColorPicker
      data-testid="square-color-picker"
      {...wrappersProps}
      {...props.triggerProps}
    >
      <ColorPicker showText {...props} />
    </StyledSquareColorPicker>
  );
};

interface StyledSquareColorPickerProps {
  $backgroundColor?: string;
}

const StyledSquareColorPicker = styled(Flex)<StyledSquareColorPickerProps>`
  .ant-color-picker-trigger {
    background: ${({ theme, $backgroundColor }) =>
      $backgroundColor ?? theme.colorBgContainerDisabled};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .ant-color-picker-color-block-inner {
    border: 1px solid ${({ theme }) => theme.colorBorder};
  }
`;
const StyledCircleColorPicker = styled(Flex)`
  .ant-color-picker-trigger {
    background: transparent;

    .ant-color-picker-color-block,
    .ant-color-picker-clear {
      border-radius: ${radius.circle};
    }
  }
`;
